import { useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  useTheme,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Stack,
  CircularProgress,
  TextField,
  MenuItem,
  Link,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { styled } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import _ from "lodash";

import { AppContext } from "context";
import { useLicenseActivation } from "./hooks";
import { ActivateDialog } from "./ActivateDialog";
import { BasicContentContainer } from "containers";
import { getLicenseStatusColor, formatDate, accessControl } from "utils";

const HeadTableCell = styled(TableCell)(({ theme }) => ({
  font: "normal normal bold 13px/14px Ubuntu",
  backgroundColor: "#F2F2F2",
  padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
  borderBottom: "1px solid #a0a0a0",
}));
const BodyTableCell = styled(TableCell)(({ theme }) => ({
  font: "normal normal normal 14px/16px Ubuntu",
  padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
}));

export const List = () => {
  const theme = useTheme();
  const { state, dispatchers } = useLicenseActivation();
  const navigate = useNavigate();
  const {
    displayLicenseList,
    licenseListLoadingStatus,
    licenseFilters,
    licenseSelectedFilters,
  } = state;
  const { userInfo } = useContext(AppContext);

  useEffect(() => {
    const roleList = _.get(userInfo, "roleList", []);
    if (accessControl.canAccessLicenseFeature(roleList)) {
      dispatchers.getLicenseList();
    } else {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (licenseListLoadingStatus) {
    return <CircularProgress />;
  }

  return (
    <>
      <Box
        sx={{
          marginTop: theme.spacing(3),
          marginLeft: theme.spacing(1),
          display: "flex",
          marginBottom: theme.spacing(3),
          "&& > *": {
            marginRight: theme.spacing(1),
          },
        }}
        noValidate
        autoComplete="off"
      >
        <TextField
          select
          label="Satus"
          variant="outlined"
          style={{ minWidth: 150 }}
          size="small"
          value={licenseSelectedFilters.status}
          onChange={(e) =>
            dispatchers.selectLicenseFilter("status", e.target.value)
          }
        >
          <MenuItem value="all">All</MenuItem>
          {licenseFilters.licenseStatusList.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="License Type"
          variant="outlined"
          style={{ minWidth: 150 }}
          size="small"
          value={licenseSelectedFilters.type}
          onChange={(e) =>
            dispatchers.selectLicenseFilter("type", e.target.value)
          }
        >
          <MenuItem value="all">All</MenuItem>
          {licenseFilters.licenseTypeList.map((licenseType) => (
            <MenuItem key={licenseType} value={licenseType}>
              {licenseType}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Key Provider"
          variant="outlined"
          style={{ minWidth: 180 }}
          size="small"
          value={licenseSelectedFilters.keyProvider}
          onChange={(e) =>
            dispatchers.selectLicenseFilter("keyProvider", e.target.value)
          }
        >
          <MenuItem value="all">All</MenuItem>
          {licenseFilters.licenseKeyProviderList.map((keyProvider) => (
            <MenuItem key={keyProvider} value={keyProvider}>
              {keyProvider}
            </MenuItem>
          ))}
        </TextField>
        <Button
          onClick={dispatchers.resetLicenseFilters}
          startIcon={<RefreshIcon />}
        >
          Reset Filters
        </Button>
      </Box>
      <BasicContentContainer>
        <Box
          sx={{
            marginTop: theme.spacing(5),
            marginBottom: theme.spacing(7),
            marginLeft: theme.spacing(4),
            marginRight: theme.spacing(4),
          }}
        >
          <ActivateDialog state={state} dispatchers={dispatchers} />
          <Button
            variant="contained"
            color="secondary"
            sx={{
              font: "normal normal normal 15px/17px Ubuntu",
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
            }}
            startIcon={<AddIcon />}
            onClick={() => dispatchers.setActivateDialogVisible(true)}
          >
            License Registration
          </Button>

          <TableContainer sx={{ marginTop: theme.spacing(3) }}>
            <Table
              sx={{
                "&& > tbody > tr > td": {
                  borderBottom: "1px solid #a0a0a0",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <HeadTableCell width={25}>NO.</HeadTableCell>
                  <HeadTableCell>Status</HeadTableCell>
                  <HeadTableCell>License Type</HeadTableCell>
                  <HeadTableCell>Key Provider</HeadTableCell>
                  <HeadTableCell>License Key</HeadTableCell>
                  <HeadTableCell>Seats</HeadTableCell>
                  <HeadTableCell>Activation Date</HeadTableCell>
                  <HeadTableCell>Expiration Date</HeadTableCell>
                  <HeadTableCell
                    sx={{
                      minWidth: 60,
                    }}
                  >
                    Action
                  </HeadTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayLicenseList.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      sx={{
                        font: "normal normal normal 14px/16px Ubuntu",
                        color: "#241540",
                        borderBottom: 0,
                      }}
                    >
                      No data to display
                    </TableCell>
                  </TableRow>
                ) : (
                  displayLicenseList.map((license, index) => (
                    <TableRow key={index}>
                      <BodyTableCell>{index + 1}</BodyTableCell>
                      <BodyTableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: "8px",
                              height: "8px",
                              background: `${getLicenseStatusColor(
                                license.status
                              )} 0% 0% no-repeat padding-box`,
                              borderRadius: "50%",
                            }}
                          />
                          <span>{license.status}</span>
                        </Stack>
                      </BodyTableCell>
                      <BodyTableCell>{license.licenseTypeName}</BodyTableCell>
                      <BodyTableCell>{license.keyProvider}</BodyTableCell>
                      <BodyTableCell sx={{ whiteSpace: "nowrap" }}>
                        {license.licenseKey}
                      </BodyTableCell>
                      <BodyTableCell>{license.seat}</BodyTableCell>
                      <BodyTableCell>
                        {formatDate(license.validStart) || "-"}
                      </BodyTableCell>
                      <BodyTableCell>
                        {formatDate(license.validEnd)}
                      </BodyTableCell>
                      <BodyTableCell>
                        {_.get(license, "daysLeft", -1) < 0 ? (
                          <Link
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                            }}
                          >
                            Renew
                          </Link>
                        ) : null}
                      </BodyTableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </BasicContentContainer>
      <Box
        sx={{
          font: "normal normal normal 14px/28px Ubuntu",
          color: "#241540",
          marginTop: theme.spacing(1),
        }}
      >
        Note: Activation dates are only available for keys provided by TXOne.
      </Box>
    </>
  );
};
